import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  mockExec, KpiCard, ChartMonth,
} from './mockData';
import KpiChartTile from './KpiChartTile';
import ForecastSplitTile from './ForecastSplitTile';
import RevExpProfitTile, { RevExpMonth } from './RevExpProfitTile';
import { execApi } from '../../../services/api';
import './styles.css';

const fmtMoney = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `₪${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `₪${Math.round(n / 1_000)}K`;
  return `₪${Math.round(n)}`;
};
const fmtInt   = (n: number) => Math.round(n).toLocaleString();
const compactInt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(Math.round(n)));
const fmtScore = (n: number) => n.toFixed(2);
const fmtPct = (n: number) => `${n.toFixed(2)}%`;

interface SegData { done: number; target: number; noshow: number; canceled: number; scheduledForward: number; refilled: number; realizationFactor: number; }
interface StaffPanelData {
  activeActual: number;
  activeTarget: number;
  hoursDone: number;        // DONE hours only (no scheduled / no-show / cancel)
  hoursTarget: number;      // plan
  scheduledForward: number; // meetings scheduled (active, future) to end of month
  noShowPct?: number;
  noShowCount?: number;
  givenHours?: number;       // opened in calendar (current month, total)
  availableHours?: number;   // truly bookable free hours = potential to raise the ceiling
  intakesActual?: number;
  intakesTarget?: number;
  fuActual?: number;
  fuTarget?: number;
  scheduledForwardIntake?: number;
  scheduledForwardFu?: number;
  scheduledForwardHours?: number;
  cancelLostPct?: number;
  realUtilization?: number;
  scheduledHours?: number;
  canceledHours?: number;
  intake?: SegData;
  followup?: SegData;
}

const rowStyle = { display: 'flex', justifyContent: 'flex-start', alignItems: 'baseline', gap: 12, padding: '7px 0', borderTop: '1px solid rgba(148,163,184,0.12)' } as const;
const labelStyle = { fontSize: 14, fontWeight: 700, color: '#475569', minWidth: 60, textAlign: 'right' } as const;
const valsStyle = { display: 'flex', flex: 1, gap: 16, fontSize: 13.5, flexWrap: 'wrap', justifyContent: 'flex-start', color: '#64748B' } as const;

function StaffPanel({
  title, type, data, onDrill,
}: { title: string; type: 'psy' | 'cm'; data: StaffPanelData; onDrill: () => void }) {
  const numH = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const seg = (label: string, s?: SegData) => {
    const v = s ?? { done: 0, target: 0, noshow: 0, canceled: 0, scheduledForward: 0, refilled: 0, realizationFactor: 0 };
    const pct = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);
    const pct1 = (n: number, d: number) => (d ? ((n / d) * 100).toFixed(2) : '0.00');   // No Show → 2 decimals
    // Each rate on its natural base (Ohad 2026-07-01):
    // ביטול% = whole-month cumulative — of ALL the month's meetings (done + upcoming
    //   scheduled + no-show + canceled), what % canceled. Consistent with utilization
    //   (both count the future). Avoids the month-start "92%" artifact.
    // נושאו% = past-only — of meetings that ACTUALLY OCCURRED (done + no-show), what %
    //   no-showed. A no-show can't happen in the future, so no `active` in the base.
    const monthTotal = v.done + v.noshow + v.canceled + v.scheduledForward;
    const occurred = v.done + v.noshow;
    return (
      <div style={rowStyle}>
        <span style={labelStyle}>{label}</span>
        <div style={valsStyle}>
          <span>יעד <b style={{ color: '#64748B' }}>{v.target.toLocaleString()}</b> · בוצע <b style={{ color: '#047857' }}>{v.done.toLocaleString()}</b></span>
          <span>No Show <b style={{ color: '#EF4444' }}>{pct1(v.noshow, occurred)}%</b></span>
          <span>ביטול <b style={{ color: '#92400E' }}>{pct(v.canceled, monthTotal)}%</b></span>
          <span>נקבעו קדימה <b style={{ color: '#475569' }}>{v.scheduledForward.toLocaleString()}</b></span>
        </div>
      </div>
    );
  };
  const ru = data.realUtilization ?? 0;
  const ruClr = ru >= 0.9 ? '#10B981' : ru >= 0.8 ? '#F59E0B' : '#EF4444';
  // Role avg No-Show % = intake + followup combined (of meetings that occurred).
  const iNs = data.intake?.noshow ?? 0, iOcc = (data.intake?.done ?? 0) + iNs;
  const fNs = data.followup?.noshow ?? 0, fOcc = (data.followup?.done ?? 0) + fNs;
  const roleNs = iOcc + fOcc > 0 ? (((iNs + fNs) / (iOcc + fOcc)) * 100).toFixed(2) : null;
  // Execution bar (Ohad 2026-07-07, refined 2026-07-12): no forecast, no target.
  //  • bar length (100%) = תואם עד סוף החודש = STANDING coordinated meetings for the month
  //    = done + noshow + scheduledForward. A no-show WAS a coordinated slot, so it's in the
  //    track (pulls done% down); canceled is NOT standing → excluded (it has its own ביטול%).
  //  • solid fill = בוצעו בלבד (done) — clean done, WITHOUT no-show and WITHOUT cancellations.
  const projBar = (s?: SegData) => {
    if (!s) return null;
    const coordinated = s.done + s.noshow + s.scheduledForward;   // תואם עד סוף החודש (standing)
    if (!coordinated) return null;
    const donePct = (s.done / coordinated) * 100;
    return (
      <div style={{ margin: '2px 0 9px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#64748B', marginBottom: 3 }}>
          <span>בוצע מול תואם</span>
          <span>
            <span style={{ color: '#047857', fontWeight: 600 }}>בוצע {s.done.toLocaleString()} · {Math.round(donePct)}%</span>
            <span style={{ color: '#475569' }}> · תואם {coordinated.toLocaleString()}</span>
          </span>
        </div>
        {/* track = coordinated max; solid fill = done */}
        <div style={{ position: 'relative', height: 7, borderRadius: 4, background: 'rgba(148,163,184,0.2)' }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: `${Math.min(100, donePct)}%`, height: '100%', background: '#10B981', borderRadius: 4 }} />
        </div>
      </div>
    );
  };
  return (
    <div className={`exec-staff-panel topic-${type === 'psy' ? 'staff' : 'staff'} clickable`} onClick={onDrill}>
      <div className="exec-staff-panel-title">{title}{ru ? <span style={{ fontSize: 14, fontWeight: 600, color: ruClr }}> · ניצולת {Math.round(ru * 100)}%</span> : null}{roleNs != null ? <span style={{ fontSize: 14, fontWeight: 600, color: '#EF4444' }}> · No Show {roleNs}%</span> : null}</div>
      <div style={{ display: 'flex', gap: 18, fontSize: 15.5, padding: '6px 0', color: '#475569', alignItems: 'baseline' }}>
        <span>{type === 'psy' ? 'רופאים' : 'מנהלי תיק'} <b style={{ color: '#047857' }}>{data.activeActual}</b><span style={{ color: '#64748B' }}>/{data.activeTarget}</span></span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>שעות</span>
        <div style={valsStyle}>
          <span>יעד <b style={{ color: '#475569' }}>{numH(data.hoursTarget)}h</b></span>
          <span>נפתחו <b style={{ color: '#475569' }}>{numH(data.givenHours ?? 0)}h</b></span>
          {data.hoursTarget > 0 && (
            <span style={{ color: (data.givenHours ?? 0) >= data.hoursTarget ? '#047857' : '#92400E' }}>
              <b>{Math.round((data.givenHours ?? 0) / data.hoursTarget * 100)}% מהיעד</b>
            </span>)}
          <span title="שעות פנויות-אמת לזימון — פוטנציאל להגדלת התקרה">פנויות לזימון <b style={{ color: '#0284C7' }}>{numH(data.availableHours ?? 0)}h</b></span>
        </div>
      </div>
      {seg('אינטייק', data.intake)}
      {projBar(data.intake)}
      {seg('מעקב', data.followup)}
      {projBar(data.followup)}
    </div>
  );
}

export default function Exec() {
  const navigate = useNavigate();
  const handleDrill = (to?: string) => { if (to) navigate(to); };
  // Dynamic "as of" month label — was a hardcoded mock ("יוני 2026"). (2026-07-01)
  const curMonthLabel = (() => {
    const heMon = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
    const now = new Date();
    return `${heMon[now.getMonth()]} ${now.getFullYear()}`;
  })();

  const byId = (id: string) => mockExec.kpis.find((k) => k.id === id)!;

  // Real staff: active therapists + DONE hours vs plan target + scheduled-forward.
  const [psyStaff, setPsyStaff] = useState<StaffPanelData | null>(null);
  const [cmStaff, setCmStaff] = useState<StaffPanelData | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toData = (c: any): StaffPanelData => ({
      activeActual: c?.active_actual ?? 0, activeTarget: c?.active_target ?? 0,
      hoursDone: c?.hours_done ?? 0, hoursTarget: c?.hours_target ?? 0,
      scheduledForward: c?.scheduled_forward ?? 0,
      givenHours: c?.given_hours ?? 0,
      availableHours: c?.available_hours ?? 0,
      intakesActual: c?.intakes_actual ?? 0, intakesTarget: c?.intakes_target ?? 0,
      fuActual: c?.followups_actual ?? 0, fuTarget: c?.followups_target ?? 0,
      scheduledForwardIntake: c?.scheduled_forward_intake ?? 0, scheduledForwardFu: c?.scheduled_forward_fu ?? 0,
      scheduledForwardHours: c?.scheduled_forward_hours ?? 0,
      cancelLostPct: c?.cancel_lost_pct ?? 0,
      realUtilization: c?.real_utilization ?? 0,
      scheduledHours: c?.scheduled_hours ?? 0,
      canceledHours: c?.canceled_hours ?? 0,
      intake: { done: c?.intake?.done ?? 0, target: c?.intake?.target ?? 0, noshow: c?.intake?.noshow ?? 0, canceled: c?.intake?.canceled ?? 0, scheduledForward: c?.intake?.scheduled_forward ?? 0, refilled: c?.intake?.refilled ?? 0, realizationFactor: c?.intake?.realization_factor ?? 0 },
      followup: { done: c?.followup?.done ?? 0, target: c?.followup?.target ?? 0, noshow: c?.followup?.noshow ?? 0, canceled: c?.followup?.canceled ?? 0, scheduledForward: c?.followup?.scheduled_forward ?? 0, refilled: c?.followup?.refilled ?? 0, realizationFactor: c?.followup?.realization_factor ?? 0 },
    });
    execApi.getStaffTargets('psy').then((r) => setPsyStaff(toData(r.data?.current))).catch(() => {});
    execApi.getStaffTargets('cm').then((r) => setCmStaff(toData(r.data?.current))).catch(() => {});
  }, []);

  // New patients = completed CM intake (done) vs plan target. Past = actual only,
  // current month = target + actual (like the financial/staff cards).
  const [newPatientsChart, setNewPatientsChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    execApi.getNewPatients()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        setNewPatientsChart(m.map((x, i) => (i === m.length - 1
          ? { label: x.label, target: x.target, actual: x.actual, coordinated: x.coordinated }
          : { label: x.label, actual: x.actual })) as ChartMonth[]);
      })
      .catch(() => { /* keep mock fallback */ });
  }, []);

  // No-show rate (%) per month — same focus+context panel (trend left, current
  // right), formatted as %. No plan target yet. (Ohad 2026-07-20)
  const [noshowTrendChart, setNoshowTrendChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    execApi.getNoshowTrend()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        setNoshowTrendChart(m.slice(-6).map((x) => ({ label: x.label, actual: x.actual })) as ChartMonth[]);
      })
      .catch(() => { /* keep empty */ });
  }, []);

  // Registrations = patients who requested the service (registered = filled the
  // intake questionnaire) per month. Top of the funnel — demand. Actual trend only.
  // (Ohad 2026-07-20)
  const [registrationsChart, setRegistrationsChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    execApi.getRegistrations()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        setRegistrationsChart(m.slice(-6).map((x) => ({ label: x.label, actual: x.actual })) as ChartMonth[]);
      })
      .catch(() => { /* keep empty */ });
  }, []);

  // Active patients = distinct patients treated (done meeting) each month — actual
  // trend only (no plan target; the in-system stock has no monthly history).
  const [activePatientsChart, setActivePatientsChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    execApi.getPatientsTreated()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        setActivePatientsChart(m.map((x, i) => (i === m.length - 1
          ? { label: x.label, actual: x.actual, coordinated: x.coordinated }
          : { label: x.label, actual: x.actual })) as ChartMonth[]);
      })
      .catch(() => { /* keep mock fallback */ });
  }, []);

  // Meetings done per month vs plan total-meetings target (psy + cm). Past = actual
  // only, current month = target + forecast (projected to full month).
  const [meetingsChart, setMeetingsChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    const now = new Date();
    const curId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    execApi.getMeetingsTarget()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        // Trailing 6 months. Current month shows יעד / תחזית (backend forecast model) / בוצע;
        // past months show their real actual only. (Ohad 2026-07-02)
        setMeetingsChart(m.slice(-6).map((x) => (x.period === curId
          ? { label: x.label, target: x.target, actual: x.actual, forecast: x.forecast, coordinated: x.coordinated }
          : { label: x.label, actual: x.actual })) as ChartMonth[]);
      })
      .catch(() => { /* keep empty */ });
  }, []);

  // Psychiatrist intakes done per month vs plan target (meetingtype 2). Same focus+context
  // format as the total-meetings + revenue tiles. (Ohad 2026-07-02)
  const [psyIntakesChart, setPsyIntakesChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    const now = new Date();
    const curId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    execApi.getPsyIntakesTarget()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        setPsyIntakesChart(m.slice(-6).map((x) => (x.period === curId
          ? { label: x.label, target: x.target, actual: x.actual, forecast: x.forecast, coordinated: x.coordinated }
          : { label: x.label, actual: x.actual })) as ChartMonth[]);
      })
      .catch(() => { /* keep empty */ });
  }, []);

  // Follow-up meetings done vs plan target, per role (meeting-side volume). Same
  // focus+context format as the intake tiles. (Ohad 2026-07-20)
  const [psyFuChart, setPsyFuChart] = useState<ChartMonth[] | null>(null);
  const [cmFuChart, setCmFuChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    const now = new Date();
    const curId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const toChart = (m: any[]) => m.slice(-6).map((x) => (x.period === curId
      ? { label: x.label, target: x.target, actual: x.actual, forecast: x.forecast, coordinated: x.coordinated }
      : { label: x.label, actual: x.actual })) as ChartMonth[];
    execApi.getFollowupsTarget('psy')
      .then((res) => setPsyFuChart(toChart(res.data ?? []))).catch(() => { /* keep empty */ });
    execApi.getFollowupsTarget('cm')
      .then((res) => setCmFuChart(toChart(res.data ?? []))).catch(() => { /* keep empty */ });
  }, []);

  // Patient satisfaction (real, DWH.dashboard_kpi_satisfaction) — overall = (psy+cm)/2, monthly.
  const [satisfactionChart, setSatisfactionChart] = useState<ChartMonth[] | null>(null);
  useEffect(() => {
    execApi.getSatisfaction()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m: any[] = res.data ?? [];
        setSatisfactionChart(m.map((x) => ({ label: x.label, actual: x.overall })) as ChartMonth[]);
      })
      .catch(() => { /* keep mock fallback */ });
  }, []);

  // Real No-Show rates (MTD) for the meetings KPI tiles, from meetings_raw.
  const [mtgNoShow, setMtgNoShow] = useState<{ psy?: { pct: number; count: number }; cm?: { pct: number; count: number } } | null>(null);
  const [cmRatio, setCmRatio] = useState<number | null>(null);  // real CM follow-ups per psy follow-up
  // Financial (revenue + expenses) chart tiles — returned 2026-08-02 (removed 13.7 as
  // mock): targets now come from the approved-plan table via /revenue-vs-target, the
  // estimated actual prices done meetings with the plan tariffs. Drill → /exec/financial.
  const [revenueChart, setRevenueChart] = useState<ChartMonth[] | null>(null);
  // הכנסות + הוצאות + רווח in one frame (Ohad 2.8): the net-only lines looked
  // parallel — the gap between revenue and expenses IS the story, so plot both
  // lines and make the gap explicit as ± profit bars. Revenue = estimated actual
  // (current month: EoM forecast); expenses = plan target (no live actual).
  const [revExpChart, setRevExpChart] = useState<RevExpMonth[] | null>(null);
  useEffect(() => {
    const now = new Date();
    const curId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    execApi.getRevenueVsTarget()
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const months: any[] = res.data?.months ?? [];
        // Trailing 6 months only — the recent trend + the current month. (Ohad 2026-07-02)
        const recent = months.slice(-6);
        // Current month shows יעד / בפועל (estimated so far) / תחזית (projected EoM);
        // a complete past month shows target + its real estimated actual.
        setRevenueChart(recent.map((m) => (m.period === curId
          ? { label: m.label, target: m.target, actual: m.estimated, forecast: m.forecast, coordinated: m.coordinated }
          : { label: m.label, target: m.target, actual: m.estimated })) as ChartMonth[]);
        setRevExpChart(recent.map((m) => {
          const isCur = m.period === curId;
          const revenue = isCur ? (m.forecast || m.estimated) : m.estimated;
          return { label: m.label, revenue, expenses: m.expense_target,
                   profit: revenue - m.expense_target, isCurrent: isCur };
        }));
      })
      .catch(() => { /* keep mock fallback */ });
  }, []);
  const revExpKpi = {
    id: 'revExpProfit', topic: 'financial', label: 'הכנסות · הוצאות · רווח', value: '',
    drillTo: '/exec/financial',
  } as KpiCard;

  useEffect(() => {
    const d = new Date();
    const cur = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    execApi.getMeetings([cur], [cur])
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const t: any[] = res.data?.noShowByType ?? [];
        const psy = t.find((x) => x.label === 'פסיכיאטריה');
        const cm = t.find((x) => x.label === 'מנהל תיק');
        setMtgNoShow({
          psy: psy ? { pct: psy.pct, count: psy.count ?? 0 } : undefined,
          cm: cm ? { pct: cm.pct, count: cm.count ?? 0 } : undefined,
        });
        setCmRatio(typeof res.data?.cmPerPsyFu === 'number' ? res.data.cmPerPsyFu : null);
      })
      .catch(() => { /* keep mock fallback */ });
  }, []);

  const meetingsKpi = { id: 'meetingsVol', topic: 'meetings', label: 'פגישות שבוצעו', value: '', drillTo: '/exec/meetings' } as KpiCard;
  const psyFuKpi = { id: 'psyFu', topic: 'meetings', label: 'פגישות מעקב פסיכיאטר', value: '', drillTo: '/exec/meetings' } as KpiCard;
  const cmFuKpi = { id: 'cmFu', topic: 'meetings', label: 'פגישות מעקב מנהל תיק', value: '', drillTo: '/exec/meetings' } as KpiCard;
  const registrationsKpi = { id: 'registrations', topic: 'patients', label: 'מלאו שאלון', value: '', drillTo: '/exec/patients' } as KpiCard;
  const psyIntakesPatientKpi = { id: 'psyIntakesPt', topic: 'patients', label: 'מטופלים שסיימו מאינטייק פסיכיאטר', value: '', drillTo: '/exec/patients' } as KpiCard;
  const noshowKpi = { id: 'noshowRate', topic: 'meetings', label: 'אחוז no-show', value: '', drillTo: '/exec/meetings' } as KpiCard;

  return (
    <div className="exec-wrap">
      <div className="exec-dash">
        <div className="exec-dash-head">
          <div className="exec-title-block">
            <h1 className="exec-title">דשבורד הנהלה</h1>
            <span className="exec-period-meta">{curMonthLabel}</span>
          </div>
        </div>

        {/* Group 1 — Financial (revenue + expenses), back since 2026-08-02 on real plan data */}
        <div className="exec-group">
          <div className="exec-group-title topic-financial">פיננסי</div>
          <div className="exec-row cols-2">
            <ForecastSplitTile
              kpi={byId('revenue')} data={revenueChart ?? []}
              onDrill={handleDrill} fmt={fmtMoney} unify
            />
            <RevExpProfitTile
              kpi={revExpKpi} data={revExpChart ?? []}
              onDrill={handleDrill} fmt={fmtMoney}
            />
          </div>
        </div>

        {/* Group 2 — Staff */}
        <div className="exec-group">
          <div className="exec-group-title topic-staff">כוח אדם · {curMonthLabel}</div>
          <div className="exec-row cols-2">
            <StaffPanel
              title="פסיכיאטרים"
              type="psy"
              data={{ ...(psyStaff ?? { activeActual: 0, activeTarget: 0, hoursDone: 0, hoursTarget: 0, scheduledForward: 0 }), noShowPct: mtgNoShow?.psy?.pct, noShowCount: mtgNoShow?.psy?.count }}
              onDrill={() => handleDrill('/exec/staff')}
            />
            <StaffPanel
              title="מנהלי תיק"
              type="cm"
              data={{ ...(cmStaff ?? { activeActual: 0, activeTarget: 0, hoursDone: 0, hoursTarget: 0, scheduledForward: 0 }), noShowPct: mtgNoShow?.cm?.pct, noShowCount: mtgNoShow?.cm?.count }}
              onDrill={() => handleDrill('/exec/staff')}
            />
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748B', marginTop: 6, justifyContent: 'flex-end' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#10B981', marginLeft: 5, verticalAlign: 'middle' }} />בפועל</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#94A3B8', marginLeft: 5, verticalAlign: 'middle' }} />יעד</span>
          </div>
        </div>

        {/* CM:Psy continuity ratio — full-width "tube", directly under the staff cards */}
        <div className="exec-group">
          <div className="exec-row cols-1">
            <div onClick={() => handleDrill('/exec/meetings')}
              style={{ background: 'var(--ex-bg-soft)', border: '1px solid var(--ex-border)', borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: '#475569', fontWeight: 700, minWidth: 150 }}>יחס מנהל תיק : פסיכיאטר</span>
              <span style={{ fontSize: 26, fontWeight: 700, color: '#475569' }}>{cmRatio != null ? cmRatio.toFixed(2) : '—'}</span>
              <div style={{ flex: 1, minWidth: 120, height: 14, borderRadius: 7, background: 'rgba(148,163,184,0.2)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, ((cmRatio ?? 0) / 5) * 100)}%`, height: '100%', background: '#2563EB', borderRadius: 7 }} />
              </div>
              <span style={{ fontSize: 12, color: '#64748B' }}>מעקבי CM לכל מעקב פסיכ׳</span>
            </div>
          </div>
        </div>

        {/* Meetings — pure VOLUME vs target (intakes moved to the patient group,
            since an intake is a patient milestone, not throughput). Total +
            follow-ups (meeting-side) + no-show rate. (Ohad 2026-07-20) */}
        <div className="exec-group">
          <div className="exec-group-title topic-meetings">פגישות שבוצעו מול יעד</div>
          <div className="exec-row cols-2">
            <ForecastSplitTile
              kpi={meetingsKpi} data={meetingsChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
            <ForecastSplitTile
              kpi={noshowKpi} data={noshowTrendChart ?? []}
              onDrill={handleDrill} fmt={fmtPct} compact={fmtPct} actualName="no-show" accent="#EF4444"
            />
          </div>
          {/* Follow-up volume — meeting-side. CM on the right so it sits above CM
              intake in the patients group below. (Ohad 2026-07-20) */}
          <div className="exec-row cols-2">
            <ForecastSplitTile
              kpi={cmFuKpi} data={cmFuChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
            <ForecastSplitTile
              kpi={psyFuKpi} data={psyFuChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
          </div>
        </div>

        {/* Group 3 — Patients as milestones (each patient counted once). Both
            intakes live here: an intake is where a patient ENTERS treatment, a
            journey milestone, not meeting volume. (Ohad 2026-07-20) */}
        <div className="exec-group">
          <div className="exec-group-title topic-patients">מטופלים</div>
          {/* Row 1 — the two intakes, sitting right under the meetings' follow-ups
              above them. Row 2 — questionnaire (demand) + treated. (Ohad 2026-07-20) */}
          <div className="exec-row cols-2">
            <ForecastSplitTile
              kpi={byId('newPatients')} data={newPatientsChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
            <ForecastSplitTile
              kpi={psyIntakesPatientKpi} data={psyIntakesChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
          </div>
          <div className="exec-row cols-2">
            <ForecastSplitTile
              kpi={registrationsKpi} data={registrationsChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
            <ForecastSplitTile
              kpi={byId('activePatients')} data={activePatientsChart ?? []}
              onDrill={handleDrill} fmt={fmtInt} compact={compactInt} actualName="בוצע" unify
            />
          </div>
        </div>

        {/* Group 5 — Quality (satisfaction trend, moved here per Ohad 2026-06-07) */}
        <div className="exec-group">
          <div className="exec-group-title topic-quality">איכות שירות</div>
          <div className="exec-row cols-1">
            <KpiChartTile
              kpi={byId('satisfaction')} data={satisfactionChart ?? []}
              onDrill={handleDrill} fmt={fmtScore} variant="line"
              yDomain={[8.0, 9.6]} hideTarget
            />
          </div>
        </div>
      </div>
    </div>
  );
}
